<script lang="ts" setup>
import { onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useSharedState } from 'composables/useSharedState';
import type { EditorProps } from './EditorProps';
import { editorStyles } from './Editor.styles';
import { loadMonaco, loadMonacoCss } from './monaco-loader';

/**
 * `<e-editor>`
 *
 * Shadow‑DOM‑safe Monaco editor wrapper:
 * - loads Monaco from a CDN (via `monaco-loader`)
 * - injects Monaco CSS into the component's shadow root
 * - syncs value / language / theme with shared state
 */
const props = withDefaults(defineProps<EditorProps>(), { value: '' });
const emit = defineEmits<{ 'update:value': [string] }>();

const state = useSharedState(props);

// Refs to the host root and the editor container in the shadow DOM.
const container = ref<HTMLElement | null>(null);
const rootContainer = ref<HTMLElement | null>(null);

let editor: monaco.editor.IStandaloneCodeEditor | null = null;
let contentDisposer: monaco.IDisposable | undefined | null = null;
let resizeObserver: ResizeObserver | null = null;

/**
 * Initialize Monaco inside the given shadow root and wire up events/layout.
 */
const initMonaco = async (shadow: ShadowRoot): Promise<void> => {
  if (!container.value) return;

  // Ensure CSS + runtime are ready.
  await loadMonacoCss(shadow);
  await loadMonaco();

  const monacoGlobal = (window as any).monaco;

  editor = monacoGlobal.editor.create(container.value, {
    value: state.value?.value ?? props.value ?? '',
    language: state.language?.value ?? 'javascript',
    theme: state.monacoTheme?.value ?? 'vs-dark',
    minimap: { enabled: false },
    automaticLayout: false
  });

  const updateHeight = () => {
    if (!editor || !container.value) return;

    const height = editor.getContentHeight();
    container.value.style.height = `${height}px`;

    // Let Monaco know the container size changed.
    editor.layout();
  };

  updateHeight();

  // Keep layout in sync with container resizes.
  resizeObserver = new ResizeObserver(() => editor?.layout());
  resizeObserver.observe(container.value);

  // Emit value changes and keep height updated.
  contentDisposer = editor?.onDidChangeModelContent(() => {
    const value = editor!.getValue();
    editor?.layout();
    emit('update:value', value);
  });
};

// Lifecycle
onMounted(async () => {
  const root = rootContainer.value?.getRootNode();
  if (!(root instanceof ShadowRoot)) {
    console.error('MonacoCE: Shadow root not found.', root);
    return;
  }

  await initMonaco(root);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  contentDisposer?.dispose();
  editor?.dispose();

  resizeObserver = null;
  contentDisposer = null;
  editor = null;
});

// Prop / state watchers
watch(
  () => state.value?.value,
  v => {
    if (editor && v != null && editor.getValue() !== v) {
      editor.setValue(v);
    }
  }
);

watch(
  () => state.language?.value,
  lang => {
    if (editor && lang) {
      editor.updateOptions({ language: lang });
    }
  }
);

watch(
  () => state.monacoTheme?.value,
  theme => {
    if (editor && theme) {
      editor.updateOptions({ theme });
    }
  }
);
</script>

<template>
  <div ref="rootContainer">
    <div
      ref="container"
      class="editor-root"
      :class="editorStyles(props)"
    ></div>
  </div>
</template>

<style scoped>
:host {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
